import { Extension, mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { DOMOutputSpec } from "@tiptap/pm/model"
import { exitCodeLikeBlockAtEnd } from "./mermaid"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		prompt: {
			insertPrompt: () => ReturnType
		}
	}
}

export const PROMPT_ACTIONS = ["copy", "cursor"] as const
export type PromptAction = (typeof PROMPT_ACTIONS)[number]

export function parsePromptActions(value: unknown): PromptAction[] {
	const list = String(value ?? "")
		.split(/[\s,]+/)
		.filter((item): item is PromptAction =>
			PROMPT_ACTIONS.includes(item as PromptAction),
		)
	const unique = [...new Set(list)]
	return unique.length > 0 ? unique : ["copy"]
}

export const cursorPromptUrl = (text: string): string =>
	`https://cursor.com/link/prompt?text=${encodeURIComponent(text)}`

/** The line the reader sees on the card; inline formatting allowed. */
export const PromptDescriptionExtension = Node.create({
	name: "promptDescription",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-prompt-description]" }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-prompt-description": "",
				class: "pl-prompt-description",
			}),
			0,
		]
	},
})

/** The prompt itself: plain text, copied as written, never shown on the portal. */
export const PromptTextExtension = Node.create({
	name: "promptText",
	content: "text*",
	marks: "",
	code: true,
	defining: true,

	parseHTML() {
		// A <pre>, so every HTML to markdown pass keeps the line breaks; ahead of
		// the code block's own rule.
		return [
			{
				tag: "pre[data-prompt-text]",
				priority: 70,
				preserveWhitespace: "full",
			},
			{ tag: "div[data-prompt-text]", preserveWhitespace: "full" },
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"pre",
			mergeAttributes(HTMLAttributes, {
				"data-prompt-text": "",
				class: "pl-prompt-text",
			}),
			0,
		]
	},
})

export const PromptExtension = Node.create({
	name: "prompt",
	group: "block",
	content: "promptDescription promptText",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			actions: {
				default: "copy",
				parseHTML: (element: HTMLElement) =>
					parsePromptActions(element.getAttribute("data-actions")).join(","),
				renderHTML: (attributes: Record<string, unknown>) => ({
					"data-actions": parsePromptActions(attributes.actions).join(","),
				}),
			},
			// Kept for the MDX round trip; the card draws no icon (icon libraries).
			icon: {
				default: null,
				parseHTML: (element: HTMLElement) => element.getAttribute("data-icon"),
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.icon ? { "data-icon": String(attributes.icon) } : {},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'div[data-type="prompt"]',
				contentElement: (node) =>
					((node as HTMLElement).querySelector?.(
						".pl-prompt-content",
					) as HTMLElement | null) ?? (node as HTMLElement),
			},
		]
	},

	renderHTML({ node, HTMLAttributes }) {
		const actions = parsePromptActions(node.attrs.actions)
		const text = node.lastChild?.textContent ?? ""
		const buttons: DOMOutputSpec[] = []
		if (actions.includes("copy")) {
			buttons.push([
				"button",
				{
					type: "button",
					class: "pl-prompt-action pl-prompt-copy",
					"data-prompt-copy": "",
					"aria-label": "Copy prompt",
				},
				[
					"span",
					{ class: "pl-prompt-labels", "aria-hidden": "true" },
					[
						"span",
						{ class: "pl-prompt-copied" },
						["span", { class: "pl-prompt-check", "aria-hidden": "true" }],
						"Copied",
					],
					["span", { class: "pl-prompt-copy-label" }, "Copy prompt"],
				],
				["span", { role: "status", class: "pl-prompt-status" }],
			])
		}
		if (actions.includes("cursor")) {
			buttons.push([
				"a",
				{
					class: "pl-prompt-action pl-prompt-cursor",
					href: cursorPromptUrl(text),
					target: "_blank",
					rel: "noopener noreferrer",
					"data-prompt-cursor": "",
				},
				["span", { class: "pl-prompt-cursor-icon", "aria-hidden": "true" }],
				"Open in Cursor",
			])
		}
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "prompt",
				class: "pl-prompt",
			}),
			["div", { class: "pl-prompt-content" }, 0],
			[
				"div",
				{ class: "pl-prompt-actions", contenteditable: "false" },
				...buttons,
			],
		]
	},

	addCommands() {
		return {
			insertPrompt:
				() =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							content: [{ type: "promptDescription" }, { type: "promptText" }],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === "promptDescription" && pos >= from - 1) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target + 1)))
							}
							return true
						})
						.run()
				},
		}
	},
})

// Ahead of the shared keymaps, so Enter and Mod-Enter reach the card first.
export const PromptKeymap = Extension.create({
	name: "promptKeymap",
	priority: 1000,

	addKeyboardShortcuts() {
		return {
			// The card holds one description, so Enter moves on to the prompt text.
			Enter: () => {
				const { $from } = this.editor.state.selection
				if ($from.parent.type.name !== "promptDescription") return false
				const textPos = $from.after()
				return this.editor
					.chain()
					.command(({ tr }) => {
						tr.setSelection(TextSelection.near(tr.doc.resolve(textPos + 1)))
						return true
					})
					.run()
			},
			ArrowDown: () =>
				exitCodeLikeBlockAtEnd(this.editor, "promptText", "prompt"),
			"Mod-Enter": () =>
				exitCodeLikeBlockAtEnd(this.editor, "promptText", "prompt"),
		}
	},
})

export const PromptExtensions = [
	PromptKeymap,
	PromptExtension,
	PromptDescriptionExtension,
	PromptTextExtension,
]
